import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useRecoilState } from "recoil";
import { v4 as uuidv4 } from "uuid";
import Modal from "react-modal";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AddQuizCard from "../../components/quiz/AddQuizCard";
import Toast from "../../components/common/Toast";
import { addQuiz, getQuiz } from "../../api";
import {
  initItem,
  quizFocusIndex,
  quizInfoContents,
  quizInfoTitle,
  quizList,
} from "../../states/atoms/quizState";
import {
  Button,
  SaveButton,
  TextArea,
  TextInput,
} from "../../components/common/Forms";
import { useUpdateEffect } from "react-use";

const QuizAddPage = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const bUpdate = pathname.indexOf("/update/") > 0;
  const params = useParams();
  const { lecture_id, qid } = params;

  const [title, setTitle] = useRecoilState(quizInfoTitle);
  const [contents, setContents] = useRecoilState(quizInfoContents);
  const [list, setList] = useRecoilState(quizList);
  const [focusIndex, setFocus] = useRecoilState(quizFocusIndex);
  const [isOpen, setOpen] = useState(false);
  const [error, setError] = useState(false);

  const queryClient = useQueryClient();
  const {
    status,
    data,
    error: err,
  } = useQuery({ queryKey: ["quiz", qid], queryFn: () => getQuiz({ qid }) });

  const quizMttn = useMutation({
    mutationFn: addQuiz,
    onSettled: async (data, error) => {
      setOpen(false);
      setError(false);
      if (data.result) {
        setOpen(true);
      } else {
        setError(error || "퀴즈 등록 중 오류가 발생했습니다.");
      }
      queryClient.invalidateQueries({ queryKey: ["quiz"] });
    },
  });

  useUpdateEffect(() => {
    if (status === "success" && data.result) {
      setError(false);
      setTitle(data.data.title);
      setContents(data.data.contents);
      const tmpList = data.data.questions.reduce((acc, cur, i) => {
        const id = uuidv4();
        if (cur.type === "text" || cur.type === "textarea") {
          acc.push({
            id,
            key: id,
            q: cur,
            option: data.data.options[i],
            answers: data.data.answers[i],
          });
        } else {
          acc.push({
            id,
            key: id,
            q: cur,
            option: { content: data.data.options[i].content },
            answers: data.data.answers[i],
          });
        }
        return acc;
      }, []);
      setList(tmpList);
      setFocus(tmpList.length - 1);
    }
  }, [status]);

  useUpdateEffect(() => {
    setError(err || false);
  }, [err]);
  try {
    const handleChangeTitle = (e) => {
      setTitle(e.target.value);
    };

    const handleChangeDescription = (e) => {
      setContents(e.target.value.split(/\r|\r\n|\n/));
    };

    const handleSave = () => {
      const [questions, options, answers] = list.reduce(
        (acc, obj) => {
          const entryItems = Object.entries(obj).reduce(
            (ac, [k, v]) =>
              ["answers", "option", "q"].includes(k) ? { ...ac, [k]: v } : ac,
            {}
          );
          const { answers: va, option: vo, q: vq } = entryItems;
          if (vq.content.length > 0) {
            return [
              [...acc[0], vq],
              [...acc[1], vo],
              [...acc[2], va],
            ];
          }
          return acc;
        },
        [[], [], []]
      );
      quizMttn.mutate({
        qid,
        lecture_id,
        title,
        contents,
        questions,
        options,
        answers,
      });
    };

    const onClick = (i) => {
      setFocus(i);
    };

    const onAdd = (data, i = -1, bEdit = false) => {
      const newList = [...list];
      const idx = i < 0 ? focusIndex : i - 1;
      newList.splice(idx, 1, data);
      if (!bEdit && i < 0) {
        newList.splice(idx + 1, 0, { ...initItem() });
      }
      setList(newList);
    };

    const onRemove = () => {
      const newList = [...list];
      newList.splice(focusIndex, 1);
      if (newList.length === 0) {
        newList.push({ ...initItem() });
      }
      setList(newList);
      onClick(focusIndex > 0 ? focusIndex - 1 : 0);
    };

    const onMove = (item, n) => {
      const idx = focusIndex;
      setFocus(-1);
      const newList = [...list];
      newList.splice(idx, 1);
      newList.splice(idx + n, 0, item);
      setList(newList);
      onClick(idx + n);
    };

    const handleClose = () => {
      setOpen(false);
      return navigate("/quiz");
    };

    return (
      <section className="min-h-screen overflow-y-auto md:mt-4">
        <div className="w-full mx-auto shadow-md overflow-hidden md:rounded-lg md:w-9/12">
          <div className="p-4 bg-cyan-500 ">
            <div className="mx-auto lg:w-full lg:mx-0">
              <div className="inline-flex items-center space-x-4 w-full">
                <TextInput
                  className="rounded-lg border-transparent flex-1 appearance-none border border-zinc-100 w-full h-12 text-3xl text-white font-bold p-2 bg-transparent shadow-sm placeholder-zinc-50 placeholder:opacity-80 "
                  id="quiz_name"
                  onChange={handleChangeTitle}
                  placeholder="퀴즈 제목을 입력하세요."
                  value={title}
                />
              </div>
            </div>
          </div>
          <div className="space-y-6 bg-white p-4">
            <TextArea
              className="flex-1 w-full p-2 bg-white shadow-sm text-sm"
              onChange={handleChangeDescription}
              placeholder="퀴즈에 대한 설명을 입력하세요."
              rows={4}
              value={contents.join("\n")}
            />
          </div>
          <div className="space-y-6 bg-white">
            <hr />
            {list.length > 0 &&
              list.map(({ id, key, q, option, answers }, i) => {
                return (
                  <AddQuizCard
                    {...{
                      id,
                      key,
                      n: i + 1,
                      q,
                      option,
                      answers,
                      focusIndex,
                      editable: focusIndex === i,
                      isFirst: i === 0,
                      isLast: i === list.length - 1,
                      onClick,
                      onAdd,
                      onRemove,
                      onMove,
                    }}
                  />
                );
              })}
            <hr />
            <div className="w-full px-4 pb-4 ml-auto">
              <SaveButton
                onClick={handleSave}
                disabled={error || !title || !list}
              >
                저장
              </SaveButton>
            </div>
          </div>
        </div>
        <Toast
          {...{
            ms: 2500,
            show: !!error,
            type: "error",
            text: error,
            onClose: () => setError(false),
          }}
        />
        <Modal
          ariaHideApp={false}
          className="h-full text-center flex"
          isOpen={isOpen}
          onRequestClose={handleClose}
        >
          <div className="card w-96 bg-white shadow-xl m-auto">
            <div className="card-body">
              <h2 className="card-title">{title}</h2>
              {!bUpdate && <p>퀴즈가 정상적으로 등록되었습니다.</p>}
              {bUpdate && <p>퀴즈가 정상적으로 수정되었습니다.</p>}
              <div className="card-actions justify-center mt-4">
                <Button type="primary" block onClick={handleClose}>
                  확인
                </Button>
              </div>
            </div>
          </div>
        </Modal>
      </section>
    );
  } catch (error) {
    console.log(error);
  }
};

export default QuizAddPage;
